import { useRef, useState } from 'react';
import type { PublicQuestion } from '../shared/protocol';
import './matching.css';

interface MatchingReveal { matchedIds?: string[]; voided: boolean }
export function Matching({ question, value, disabled, reveal, onChange }: {
  question: PublicQuestion; value: string[]; disabled: boolean; reveal: MatchingReveal | null;
  onChange: (value: string[]) => void;
}) {
  const [selectedFlag, setSelectedFlag] = useState<number | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [ghost, setGhost] = useState<{ index: number; x: number; y: number } | null>(null);
  const drag = useRef<{ index: number; x: number; y: number; moved: boolean } | null>(null);
  const suppressClick = useRef<number | null>(null);
  const items = question.items ?? [];
  const targets = question.targets ?? [];
  const countryName = (id: string) => items.find(item => item.id === id)?.text ?? '';
  const assign = (flagIndex: number, countryId: string) => {
    if (disabled || reveal || !items.some(item => item.id === countryId)) return;
    const next = Array.from({ length: targets.length }, (_, index) => value[index] ?? '');
    const oldFlagIndex = next.indexOf(countryId);
    if (oldFlagIndex >= 0 && oldFlagIndex !== flagIndex) next[oldFlagIndex] = next[flagIndex];
    next[flagIndex] = countryId;
    onChange(next); setSelectedFlag(null); setSelectedCountry(null);
  };
  const chooseCountry = (id: string) => {
    if (disabled || reveal) return;
    if (selectedFlag !== null) assign(selectedFlag, id);
    else setSelectedCountry(current => current === id ? null : id);
  };
  const flagButton = (index: number, inTray = false) => <button type="button" className="pg-match__flag"
    data-selected={selectedFlag === index} data-placed={inTray && !!value[index]} disabled={disabled}
    aria-label={`Flag ${index + 1}${value[index] ? ` placed beside ${countryName(value[index])}` : ''}`} aria-pressed={selectedFlag === index}
    onClick={() => {
      if (suppressClick.current === index) { suppressClick.current = null; return; }
      if (selectedCountry) assign(index, selectedCountry);
      else setSelectedFlag(current => current === index ? null : index);
    }}
    onPointerDown={event => {
      if (disabled || event.button !== 0) return;
      suppressClick.current = null;
      drag.current = { index, x: event.clientX, y: event.clientY, moved: false };
      event.currentTarget.setPointerCapture(event.pointerId);
    }}
    onPointerMove={event => {
      const active = drag.current;
      if (!active) return;
      if (Math.abs(event.clientX - active.x) + Math.abs(event.clientY - active.y) > 8) active.moved = true;
      if (active.moved) setGhost({ index: active.index, x: event.clientX, y: event.clientY });
    }}
    onPointerUp={event => {
      const active = drag.current; drag.current = null; setGhost(null);
      if (active?.moved) {
        const row = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>('[data-country-slot]');
        if (row?.dataset.countrySlot) assign(active.index, row.dataset.countrySlot);
        suppressClick.current = active.index;
      }
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    }}
    onPointerCancel={() => { drag.current = null; setGhost(null); }}>
    <img src={targets[index].imageUrl} alt={`Flag ${index + 1}`} draggable={false} />
    <span>{inTray && value[index] ? 'Placed' : `Flag ${index + 1}`}</span>
  </button>;
  return <section className="pg-match" aria-label="Flag and country matches">
    {!reveal && <>
      <p className="ex-help pg-match__help">Drag flags beside the countries. Or tap a flag, then a country.</p>
      <div className="pg-match__rows" role="group" aria-label="Country matching rows">
        <span className="pg-match__heading">Flag</span><span className="pg-match__heading">Country</span>
        {items.map(item => {
          const index = value.indexOf(item.id);
          return <div className="pg-match__row" key={item.id} data-country-slot={item.id} data-selected={selectedCountry === item.id}>
            <div className="pg-match__slot">
              {index >= 0 ? flagButton(index) : <button type="button" className="pg-match__empty" disabled={disabled} onClick={() => chooseCountry(item.id)} aria-label={`Place a flag beside ${item.text}`}>Drop flag here</button>}
              {index >= 0 && !disabled && <button className="pg-match__clear" type="button" aria-label={`Remove flag beside ${item.text}`} onClick={() => { const next = [...value]; next[index] = ''; onChange(next); }}>×</button>}
            </div>
            <button type="button" className="pg-match__country" disabled={disabled} aria-pressed={selectedCountry === item.id} onClick={() => chooseCountry(item.id)}>{item.text}</button>
          </div>;
        })}
      </div>
      <div className="pg-match__tray" role="group" aria-label="Flag tray">{targets.map((target, index) => <div key={target.id}>{flagButton(index, true)}</div>)}</div>
      {ghost && <img className="pg-match__ghost" src={targets[ghost.index].imageUrl} alt="" style={{ left: ghost.x, top: ghost.y }} />}
    </>}
    {reveal && <div className="pg-match__reveal" aria-label="Official flag matches">
      {targets.map((target, index) => {
        const correctId = reveal.matchedIds?.[index] ?? '';
        const chosenId = value[index] ?? '';
        const correctName = countryName(correctId);
        return <article className="pg-match__revealed-target" data-correct={!!correctId && chosenId === correctId} data-incorrect={!!chosenId && chosenId !== correctId} key={target.id}>
          <span className="pg-match__flag-index">Flag {index + 1}</span>
          <img src={target.imageUrl} alt={correctName ? `Flag ${index + 1}: ${correctName}` : `Flag ${index + 1}`} />
          <strong>{correctName || 'Answer unavailable'}</strong>
          {chosenId && chosenId !== correctId && <span className="pg-match__your-choice">Your match: {countryName(chosenId)}</span>}
        </article>;
      })}
    </div>}
  </section>;
}
